// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// Canvas rendering: the live before/after waveforms and the shareable report card.

import { formatDuration } from './audio';
import type { DenoiseResult } from './types';

const ACCENT = '#34e6bf';
const MUTED = '#5b6b64';

/** Draw a min/max peak waveform of `samples` into a canvas, fitted to width. */
export function drawWaveform(
  canvas: HTMLCanvasElement,
  samples: Float32Array,
  color: string,
): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const cssW = canvas.clientWidth || 600;
  const cssH = canvas.clientHeight || 96;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, cssW, cssH);

  const mid = cssH / 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, mid);
  ctx.lineTo(cssW, mid);
  ctx.stroke();

  if (samples.length === 0) return;
  const step = samples.length / cssW;
  ctx.fillStyle = color;
  for (let x = 0; x < cssW; x++) {
    const start = Math.floor(x * step);
    const end = Math.min(samples.length, Math.floor((x + 1) * step));
    let min = 1;
    let max = -1;
    for (let i = start; i < end; i++) {
      const v = samples[i];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (end <= start) {
      min = max = samples[Math.min(start, samples.length - 1)] || 0;
    }
    const y1 = mid - max * mid * 0.94;
    const y2 = mid - min * mid * 0.94;
    ctx.fillRect(x, y1, 1, Math.max(1, y2 - y1));
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function waveformInto(
  ctx: CanvasRenderingContext2D,
  samples: Float32Array,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  const mid = y + h / 2;
  ctx.fillStyle = color;
  if (samples.length === 0) return;
  const step = samples.length / w;
  for (let i = 0; i < w; i++) {
    const start = Math.floor(i * step);
    const end = Math.min(samples.length, Math.floor((i + 1) * step));
    let min = 1;
    let max = -1;
    for (let k = start; k < end; k++) {
      const v = samples[k];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (end <= start) min = max = 0;
    const y1 = mid - max * (h / 2) * 0.94;
    const y2 = mid - min * (h / 2) * 0.94;
    ctx.fillRect(x + i, y1, 1, Math.max(1, y2 - y1));
  }
}

/** Render a 1200×675 shareable PNG summarising the clean-up. */
export async function renderReportCard(
  result: DenoiseResult,
  sourceName: string,
): Promise<Blob> {
  const W = 1200;
  const H = 675;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unsupported');

  // Background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0f1a17');
  bg.addColorStop(1, '#0b0f0e');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Header
  ctx.fillStyle = ACCENT;
  ctx.font = '700 40px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
  ctx.fillText('Hushwell', 64, 92);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '400 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
  ctx.fillText('Voice noise removed — on-device', 64, 128);

  const reduction = Math.max(0, result.noiseFloorInDb - result.noiseFloorOutDb);

  // Big number
  ctx.fillStyle = '#ffffff';
  ctx.font = '800 118px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
  ctx.fillText(`−${reduction.toFixed(0)} dB`, 60, 300);
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font = '500 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
  ctx.fillText('quieter background', 66, 344);

  // Before / after waveform panels
  const panelY = 400;
  const panelH = 150;
  const panelW = 1072;
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  roundRect(ctx, 64, panelY, panelW, panelH, 16);
  ctx.fill();

  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = '600 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
  ctx.fillText('BEFORE', 84, panelY + 34);
  ctx.fillText('AFTER', 84, panelY + panelH / 2 + 44);

  waveformInto(ctx, result.dry, 200, panelY + 10, 920, panelH / 2 - 18, MUTED);
  waveformInto(ctx, result.wet, 200, panelY + panelH / 2 + 8, 920, panelH / 2 - 18, ACCENT);

  // Footer
  ctx.fillStyle = 'rgba(255,255,255,0.42)';
  ctx.font = '400 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
  const dur = formatDuration(result.dry.length / result.sampleRate);
  const clip = sourceName.length > 42 ? sourceName.slice(0, 41) + '…' : sourceName;
  ctx.fillText(`${clip}  ·  ${dur}  ·  nothing uploaded`, 64, 620);
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.textAlign = 'right';
  ctx.fillText('hushwell.benrichardson.dev', W - 64, 620);
  ctx.textAlign = 'left';

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not render report'))),
      'image/png',
    );
  });
}
